/**
 * Подготовка снимка чека к отправке.
 *
 * Три задачи, и каждая нашлась на настоящих фотографиях (§6.11).
 *
 * Поворот. Три снимка из четырёх пришли кадром 4032×3024 при вертикальном
 * чеке. Браузер применяет поворот из EXIF при отрисовке, поэтому пересъём
 * через холст отдаёт картинку уже в правильную сторону — и распознаванию не
 * приходится гадать.
 *
 * Размер. Предел разбора — 10 МБ, снимок с телефона доходит до четырёх, и
 * гонять их целиком по мобильной сети незачем. Сторона в 2000 точек выбрана
 * с запасом: на проверенном чеке хватало и 1200, но у длинного чека, снятого
 * издалека, на строку приходится меньше точек.
 *
 * Формат. JPEG вместо HEIC: распознавание принимает jpeg, png и webp.
 */

/** Длинная сторона для распознавания. */
const MAX_SIDE = 2000;
/**
 * Длинная сторона для архива.
 *
 * Снимок для распознавания весит около мегабайта, и сотня чеков забила бы
 * хранилище устройства. Для «посмотреть, что было куплено» хватает вдвое
 * меньшей стороны и вчетверо меньшего веса.
 */
export const ARCHIVE_SIDE = 1100;
const QUALITY = 0.85;

export interface PreparedImage {
  /** Base64 без префикса data: — сервер ждёт голое содержимое. */
  base64: string;
  mimeType: 'image/jpeg';
  width: number;
  height: number;
  bytes: number;
}

export async function prepareReceipt(file: File, maxSide = MAX_SIDE): Promise<PreparedImage> {
  const src = await decode(file);
  const scale = Math.min(1, maxSide / Math.max(src.width, src.height));
  const width = Math.max(1, Math.round(src.width * scale));
  const height = Math.max(1, Math.round(src.height * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Браузер не дал холст для обработки снимка');
  // Белый фон: у JPEG нет прозрачности, и без заливки края стали бы чёрными.
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(src.image as CanvasImageSource, 0, 0, width, height);
  if ('close' in src.image && typeof src.image.close === 'function') src.image.close();

  const dataUrl = canvas.toDataURL('image/jpeg', QUALITY);
  const base64 = dataUrl.slice(dataUrl.indexOf(',') + 1);
  return { base64, mimeType: 'image/jpeg', width, height, bytes: Math.floor((base64.length * 3) / 4) };
}

interface Decoded { image: ImageBitmap | HTMLImageElement; width: number; height: number }

/**
 * Разбор файла в картинку.
 *
 * createImageBitmap быстрее и не держит элемент в разметке, но поддержка
 * imageOrientation неровная. Поэтому запасной путь через <img>: браузеры
 * применяют поворот из EXIF при отрисовке сами.
 */
async function decode(file: File): Promise<Decoded> {
  if (typeof createImageBitmap === 'function') {
    try {
      const bmp = await createImageBitmap(file, { imageOrientation: 'from-image' });
      return { image: bmp, width: bmp.width, height: bmp.height };
    } catch {
      // Старый браузер или неподдержанный формат — идём запасным путём.
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.decoding = 'sync';
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('Снимок не открылся: попробуй другой файл'));
      img.src = url;
    });
    return { image: img, width: img.naturalWidth, height: img.naturalHeight };
  } finally {
    URL.revokeObjectURL(url);
  }
}
